import type { PolicyPeriodStatus } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { OnlineSales } from 'gw-edge-client';
import { useRecoilValue } from 'recoil';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { allCoverablesAtom } from '../recoil/atoms/allCoverablesAtom';
import { allCoverablesToCoverableDTOArray } from '../utils/coverablesUtil';
import type { JobStatusDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { wait } from '../utils/asyncUtil';

const RETRY_TIMEOUT = 1000;
const MAX_RETRIES = 10;
const MAX_INCREASE_RATE = 16;

const canProceedToReceiptPage = (
    isPendingCompletion: boolean,
    status: PolicyPeriodStatus,
    allowProceedingForQuoted: boolean
): boolean => {
    return (
        (isPendingCompletion && status === 'Quoted') ||
        (!isPendingCompletion && status === 'Bound') ||
        (!isPendingCompletion && status === 'Quoted' && allowProceedingForQuoted)
    );
};

const updateJobNumber = async (jobNumber: string, jobStatus: JobStatusDTO): Promise<string> => {
    if (jobStatus.status !== 'Withdrawn' || !jobStatus.policyNumber) {
        return jobNumber;
    }

    return await OnlineSales.getLatesBoundTransactionIdByPolicyNumber(jobStatus.policyNumber);
};

export function useAwaitBoundJob(isPendingCompletion: boolean): {
    awaitBoundJob: () => Promise<JobStatusDTO>;
} {
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);

    const allCoverables = useRecoilValue(allCoverablesAtom);
    const coverables = allCoverablesToCoverableDTOArray(allCoverables);
    const allowProceedingForQuoted = coverables.every(
        (cov) => cov.willSpinOff || (cov.status === 'Removed' && cov.lobCode === 'MVP')
    );

    const awaitBoundJob = async (): Promise<JobStatusDTO> => {
        let retries = 0;
        const endPointResponse = await OnlineSales.getJobStatus(jobNumber, sessionId);
        let response = endPointResponse;
        let status = response.status;
        let latestJobNumber;

        if (canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted))
            return response;

        while (retries <= MAX_RETRIES) {
            latestJobNumber = await updateJobNumber(jobNumber, response);
            const exponentialBackoffRate = Math.pow(2, retries);
            const delayIncreaseRate =
                exponentialBackoffRate > MAX_INCREASE_RATE
                    ? MAX_INCREASE_RATE
                    : exponentialBackoffRate;
            await wait(RETRY_TIMEOUT * delayIncreaseRate);

            try {
                const retryResponse = await OnlineSales.getJobStatus(latestJobNumber, sessionId);
                response = retryResponse;
                status = retryResponse.status;
            } catch (err) {
                if (err instanceof Error) throw err;
            }

            if (canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted)) {
                break;
            }

            retries++;
        }

        if (!canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted)) {
            throw new Error('Exceeded max retries for awaitBoundJob.');
        }
        return response;
    };

    return { awaitBoundJob };
}
